'use client';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { SignUpFormData } from './SignUpFlow';
import { createClient } from '@/lib/supabase/client';

interface StepPersonalProps {
  form: UseFormReturn<SignUpFormData>;
  formData: Partial<SignUpFormData>;
  onNext: (data: Partial<SignUpFormData>) => void;
}

export default function StepPersonal({ form, onNext }: StepPersonalProps) {
  const { register, handleSubmit, watch, formState: { errors } } = form;
  const emailValue = watch('email');
  const supabase = createClient();

  const onSubmit = async (data: SignUpFormData) => {
    // Check email uniqueness against members table
    const { data: existing } = await supabase
      .from('members')
      .select('id')
      .eq('email', data.email)
      .maybeSingle();

    if (existing) {
      toast.error('This email is already registered. Please sign in instead.');
      return;
    }

    onNext({ fullName: data.fullName, email: data.email, phone: data.phone });
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="font-display font-bold text-2xl text-white mb-2">Personal Information</h2>
        <p className="text-[#9CA3AF] text-sm">
          This information will be used to create your member account.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div>
          <label className="label">Full Legal Name</label>
          <p className="text-xs text-[#6B7280] mb-2">As it appears on your national ID or passport</p>
          <input
            type="text"
            className={`input-field ${errors.fullName ? 'input-error' : ''}`}
            placeholder="e.g. Marcus Emmanuel Williams"
            {...register('fullName', {
              required: 'Full name is required',
              minLength: { value: 3, message: 'Enter your full legal name' },
            })}
          />
          {errors.fullName && <p className="text-[#EF4444] text-xs mt-1.5">{errors.fullName.message}</p>}
        </div>

        <div>
          <label className="label">Email Address</label>
          <p className="text-xs text-[#6B7280] mb-2">You will use this to log in to your dashboard</p>
          <input
            type="email"
            className={`input-field ${errors.email ? 'input-error' : ''}`}
            placeholder="you@example.com"
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
            })}
          />
          {errors.email && <p className="text-[#EF4444] text-xs mt-1.5">{errors.email.message}</p>}
        </div>

        <div>
          <label className="label">Confirm Email Address</label>
          <input
            type="email"
            className={`input-field ${errors.confirmEmail ? 'input-error' : ''}`}
            placeholder="Re-enter your email"
            {...register('confirmEmail', {
              required: 'Please confirm your email',
              validate: (val) => val === emailValue || 'Email addresses do not match',
            })}
          />
          {errors.confirmEmail && <p className="text-[#EF4444] text-xs mt-1.5">{errors.confirmEmail.message}</p>}
        </div>

        <div>
          <label className="label">Phone Number</label>
          <p className="text-xs text-[#6B7280] mb-2">Saint Lucia number — include country code (+1-758)</p>
          <input
            type="tel"
            className={`input-field ${errors.phone ? 'input-error' : ''}`}
            placeholder="+1-758-XXX-XXXX"
            {...register('phone', {
              required: 'Phone number is required',
              minLength: { value: 7, message: 'Enter a valid phone number' },
            })}
          />
          {errors.phone && <p className="text-[#EF4444] text-xs mt-1.5">{errors.phone.message}</p>}
        </div>

        <button type="submit" className="btn-primary w-full justify-center py-3.5 mt-2">
          Continue
          <Icon name="ArrowRightIcon" size={18} />
        </button>
      </form>
    </div>
  );
}